import { useEffect, useRef, useState } from 'react'
import React from 'react'

import { InfoIcon } from 'lucide-react'
import { Tooltip } from 'react-tooltip'

import ChainToken from '@src/components/ChainToken'
import ClockIcon from '@src/components/icons/Clock'
import ExchangeIcon from '@src/components/icons/Exchange'
import PointIcon from '@src/components/icons/Point'
import SandDollarIcon from '@src/components/icons/SandDollar'
import SuccessIcon from '@src/components/icons/Success'
import { ChainId } from '@src/configs/chainInfo'
import { SOLANA_TOKEN_ACCOUNT_TOOLTIP } from '@src/constants/exchange'
import { LiquiditySource } from '@src/enums'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { DestinationTxComponent } from '@src/pages/Exchange/components/SwapInProgressFlow/DestinationTxComponent'
import { SourceTxComponent } from '@src/pages/Exchange/components/SwapInProgressFlow/SourceTxComponent'
import SwapModalFooter from '@src/pages/Exchange/components/SwapInProgressFlow/SwapModalFooter'
import { analyticsService } from '@src/services/analytics'
import { IExchangeData } from '@src/types/exchange'
import { ISandDollarsData } from '@src/types/order'
import { computeUSDAmount } from '@src/utils/balance'
import { isFeatureEnabled } from '@src/utils/environment'
import { formatExchangeAmountWithCommas, isPotentiallyCCTP, isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'
import { formatNumber } from '@src/utils/formatFee'
import { createSandDollarsBalance } from '@src/utils/sandDollars'
import { useConfigStore } from '@src/zustand_stores'

interface ISwapCompletedProps {
    sellData: IExchangeData
    buyData: IExchangeData
    result?: {
        sourceTxHash?: string
        destinationTxHash?: string
        // V2 API fields
        place_tx_id?: string
        fill_tx_id?: string
    }
    miles?: number
    sandDollars?: ISandDollarsData
    fullTradeTimeTimer?: { seconds: number; minutes: number; hours: number } | NodeJS.Timeout
    liquiditySource?: LiquiditySource
    onClose?: () => void
}

export default function SwapCompleted({
    sellData,
    buyData,
    result = undefined,
    miles,
    sandDollars,
    fullTradeTimeTimer,
    liquiditySource,
    onClose,
}: ISwapCompletedProps) {
    // Track when the SwapCompleted component is rendered with confirmed completion
    const [eventTracked, setEventTracked] = useState(false)
    const eventAttempts = useRef(0)

    // Check if AI was used to generate this quote
    const wasAIUsed = localStorage.getItem('ai_quote_generated') === 'true'

    // Check if custom recipient was used
    const wasCustomRecipientUsed = localStorage.getItem('custom_recipient_used') === 'true'

    // Get wallet connections at the component level
    const { evmWalletAddress, solanaWalletAddress, tronWalletAddress } = useWalletConnections()

    // Create Balance object from sand dollars data
    const sandDollarsBalance = createSandDollarsBalance(sandDollars)

    // Track event when component mounts
    useEffect(() => {
        trackCompletionEvent()

        // Also track when visibility changes (user tabs back in)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                trackCompletionEvent()
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [])

    // Function to track the completion event
    const trackCompletionEvent = () => {
        // Only track once
        if (eventTracked) return

        // Increment attempt counter for debugging
        eventAttempts.current += 1

        // Only track the frontend completion event if we have confirmation from the backend
        // (via a destination transaction hash which indicates backend confirmation)
        if (result?.destinationTxHash || result?.fill_tx_id) {
            // Determine if this is a CCTP swap (USDC to USDC across chains)
            const isCCTP = isPotentiallyCCTP(
                sellData.token?.symbol,
                buyData.token?.symbol,
                sellData.chainId,
                buyData.chainId,
                liquiditySource
            )

            // Calculate USD equivalents
            const sourceAmountUSD = computeUSDAmount(sellData.token, sellData.value)
            const destinationAmountUSD = computeUSDAmount(buyData.token, buyData.value)

            // Track swap completion with regular PostHog event
            analyticsService.trackEvent('trade_completed_fe', {
                source_token: sellData.token?.symbol,
                source_chain: sellData.chainId,
                source_amount: sellData.value.toString(),
                source_amount_usd: sourceAmountUSD,
                source_token_price: sellData.token?.price_current,
                destination_token: buyData.token?.symbol,
                destination_chain: buyData.chainId,
                destination_amount: buyData.value.toString(),
                destination_amount_usd: destinationAmountUSD,
                destination_token_price: buyData.token?.price_current,
                miles_earned: miles,
                sand_dollars_earned: sandDollars?.amount,
                swap_time: formatTime(),
                has_destination_tx: !!(result?.destinationTxHash || result?.fill_tx_id),
                place_tx: sourceTxHash,
                fill_tx: destinationTxHash,
                src_chain_id: sellData.chainId.toString(),
                dst_chain_id: buyData.chainId.toString(),
                is_cctp: isCCTP,
                jupiter_swap: liquiditySource === LiquiditySource.jupiter,
                swap_type: isCCTP ? 'cctp' : liquiditySource === LiquiditySource.jupiter ? 'jupiter' : 'market_maker',
                backend_confirmed: true,
                attempts: eventAttempts.current,
                was_tabbed_in: document.visibilityState === 'visible',
                ai_quote_used: wasAIUsed,
                custom_recipient_used: wasCustomRecipientUsed,
            })

            // Get the connected wallet address for tracking
            const walletAddress =
                evmWalletAddress ||
                (solanaWalletAddress ? solanaWalletAddress.toString() : undefined) ||
                tronWalletAddress

            // Track swap completion with Twitter conversion event
            analyticsService.trackSwapCompletionWithTwitter(
                sellData.token?.symbol || 'Unknown',
                sellData.chainId,
                sellData.value.toString(),
                sourceAmountUSD || 0,
                buyData.token?.symbol || 'Unknown',
                buyData.chainId,
                buyData.value.toString(),
                destinationAmountUSD || 0,
                sourceTxHash || destinationTxHash || result?.place_tx_id || result?.fill_tx_id, // Transaction ID
                walletAddress, // Wallet address
                {
                    // Additional data
                    miles_earned: miles,
                    sand_dollars_earned: sandDollars?.amount,
                    is_cctp: isCCTP,
                    jupiter_swap: liquiditySource === LiquiditySource.jupiter,
                    swap_type: isCCTP
                        ? 'cctp'
                        : liquiditySource === LiquiditySource.jupiter
                          ? 'jupiter'
                          : 'market_maker',
                    backend_confirmed: true,
                }
            )

            // Update wallet value after transaction completes
            // Using the same walletAddress for consistency

            if (walletAddress) {
                // Determine chain type based on source chain
                let chainType = 'EVM'
                if (sellData.chainId === ChainId.Solana) {
                    chainType = 'SVM'
                } else if (sellData.chainId === ChainId.Tron) {
                    chainType = 'TRX'
                }

                // Track transaction completion in PostHog
                // Note: We don't have the transaction value here, so we're just tracking the completion
                analyticsService.trackTransactionCompletion(walletAddress, chainType)
            }

            setEventTracked(true)
        } else {
            // If we don't have backend confirmation yet, track as a partial completion
            // Determine if this is a CCTP swap (USDC to USDC across chains)
            const isCCTP = isPotentiallyCCTP(
                sellData.token?.symbol,
                buyData.token?.symbol,
                sellData.chainId,
                buyData.chainId,
                liquiditySource
            )

            // Calculate USD equivalents
            const sourceAmountUSD = computeUSDAmount(sellData.token, sellData.value)
            const destinationAmountUSD = computeUSDAmount(buyData.token, buyData.value)

            analyticsService.trackEvent('trade_completion_view', {
                source_token: sellData.token?.symbol,
                source_chain: sellData.chainId,
                source_amount: sellData.value.toString(),
                source_amount_usd: sourceAmountUSD,
                source_token_price: sellData.token?.price_current,
                destination_token: buyData.token?.symbol,
                destination_chain: buyData.chainId,
                destination_amount: buyData.value.toString(),
                destination_amount_usd: destinationAmountUSD,
                destination_token_price: buyData.token?.price_current,
                miles_earned: miles,
                sand_dollars_earned: sandDollars?.amount,
                swap_time: formatTime(),
                has_destination_tx: false,
                place_tx: sourceTxHash,
                fill_tx: null,
                src_chain_id: sellData.chainId.toString(),
                dst_chain_id: buyData.chainId.toString(),
                is_cctp: isCCTP,
                swap_type: isCCTP ? 'cctp' : 'market_maker',
                backend_confirmed: false,
                attempts: eventAttempts.current,
                was_tabbed_in: document.visibilityState === 'visible',
                ai_quote_used: wasAIUsed,
                custom_recipient_used: wasCustomRecipientUsed,
            })
        }
    }

    // Track event when component mounts
    useEffect(() => {
        trackCompletionEvent()

        // Also track when visibility changes (user tabs back in)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                trackCompletionEvent()
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [])

    const availableNetworks = useConfigStore(config => config.availableNetworks)
    // Get transaction hashes from either V1 or V2 API format
    const sourceTxHash = result?.sourceTxHash || ''
    const destinationTxHash = result?.destinationTxHash || ''

    // No longer need to format transaction hashes here as it's handled in the components

    const isDestinationSolana = buyData.chainId === ChainId.Solana

    // Format the time string
    const formatTime = () => {
        if (!fullTradeTimeTimer) return '0s'

        // Check if fullTradeTimeTimer is the timer object with time properties
        if (typeof fullTradeTimeTimer === 'object' && 'seconds' in fullTradeTimeTimer) {
            const { seconds, minutes, hours } = fullTradeTimeTimer
            if (hours > 0) {
                return `${hours}h ${minutes}m ${seconds}s`
            } else if (minutes > 0) {
                return `${minutes}m ${seconds}s`
            } else {
                return `${seconds}s`
            }
        }

        // Fallback if it's not the expected object
        return '0s'
    }

    useEffect(() => {
        return () => {
            // Clear the timer if it exists
            if (fullTradeTimeTimer && typeof fullTradeTimeTimer !== 'object') {
                clearInterval(fullTradeTimeTimer)
            }
            // Clear AI quote flag when component unmounts
            localStorage.removeItem('ai_quote_generated')
            // Clear custom recipient flag when component unmounts
            localStorage.removeItem('custom_recipient_used')
        }
    }, [fullTradeTimeTimer])

    return (
        <div className='px-4'>
            <div className='space-y-4'>
                <div className='flex w-full items-center justify-center gap-[8px] px-1'>
                    <SuccessIcon className='h-6 w-6' />
                    <p className='flex-10 text-xl font-medium text-mach-green'>Swap Complete</p>
                </div>
                <div className='relative flex'>
                    <img src={'/assets/image/ticket.png'} alt='' />
                    <div className='absolute left-0 top-0 h-full w-full p-4'>
                        <div className='flex flex-col items-center justify-center gap-4'>
                            <img src={'/assets/image/plane.png'} alt='' />
                            <div
                                className='relative h-[131px] w-full rounded-lg border-[]'
                                style={{
                                    background:
                                        'conic-gradient(from 180deg at 50% 50%, #3E73C41A 0deg, #349FFD1A 144deg, rgba(52, 159, 253, 0.1) 227.82deg, #3E73C41A 360deg)',
                                    boxShadow:
                                        '0px 4px 4px rgba(0, 0, 0, 0.25), 0px -1px 1px #000000, inset 0px 1px 0px rgba(255, 255, 255, 0.25)',
                                }}
                            >
                                <div className='absolute inset-0 rounded-xl border border-[#349FFD] blur-[2px]'></div>
                                <div className='mt-[36px] flex gap-[10px] px-[15px]'>
                                    <div className='flex min-w-0 flex-1 flex-col gap-y-2'>
                                        <div className='flex items-center gap-x-1'>
                                            <ChainToken
                                                tokenLogo={sellData.token?.logoURI}
                                                networkIcon={availableNetworks[sellData.chainId]?.icon}
                                                tokenIconSize={32}
                                                chainIconSize={12}
                                            />
                                            <span className='xs:max-w-20 xs:text-2xl max-w-[4.5rem] truncate text-xl font-medium'>
                                                {sellData.token?.symbol}
                                            </span>
                                        </div>
                                        <span className='xs:text-lg text-base'>
                                            {formatExchangeAmountWithCommas(sellData)}
                                        </span>
                                    </div>
                                    <div className='xs:gap-4 mx-auto flex w-1/3 flex-col items-center justify-center gap-2'>
                                        <div className='flex items-center'>
                                            <div
                                                className='h-[1px] w-1'
                                                style={{
                                                    background:
                                                        'linear-gradient(270deg, rgba(37, 38, 43, 0.7) 0%, rgba(125, 128, 145, 0.2) 100%)',
                                                }}
                                            ></div>
                                            <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[#0F1011]'>
                                                <ExchangeIcon className='h-4 w-4' />
                                            </div>
                                            <div
                                                className='h-[1px] w-1'
                                                style={{
                                                    background:
                                                        'linear-gradient(270deg, rgba(37, 38, 43, 0.7) 0%, rgba(125, 128, 145, 0.2) 100%)',
                                                }}
                                            ></div>
                                        </div>
                                        <div className='flex items-center justify-center gap-2'>
                                            <ClockIcon />
                                            <span className='xs:text-xs text-[10px] font-medium text-mach-sub-text'>
                                                Arrival: {formatTime()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className='flex min-w-0 flex-1 flex-col gap-y-2 self-end'>
                                        <div className='flex items-center justify-end gap-x-1'>
                                            <ChainToken
                                                tokenLogo={buyData.token?.logoURI}
                                                networkIcon={availableNetworks[buyData.chainId]?.icon}
                                                tokenIconSize={32}
                                                chainIconSize={12}
                                            />
                                            <span className='xs:max-w-20 xs:text-2xl max-w-[4.5rem] truncate text-xl font-medium'>
                                                {buyData.token?.symbol}
                                            </span>
                                        </div>
                                        <div className='flex items-center justify-end gap-1'>
                                            {isDestinationSolana && liquiditySource !== LiquiditySource.jupiter && (
                                                <>
                                                    <span
                                                        className='cursor-help text-gray-500'
                                                        id='solana-token-account-tooltip-completed'
                                                    >
                                                        <InfoIcon size={20} />
                                                    </span>
                                                    <Tooltip
                                                        anchorId='solana-token-account-tooltip-completed'
                                                        place='top'
                                                        className='z-[9999] max-w-xs rounded-md bg-gray-800 p-2 text-xs text-white shadow-lg'
                                                        positionStrategy='fixed'
                                                    >
                                                        {SOLANA_TOKEN_ACCOUNT_TOOLTIP}
                                                    </Tooltip>
                                                </>
                                            )}
                                            <span className='xs:text-lg text-right text-base'>
                                                {formatExchangeAmountWithCommas(buyData)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='xs:mt-8 xs:gap-7 mt-6 flex flex-col gap-4'>
                            <span className='xs:text-xl mb-2 text-lg font-medium text-mach-accent-forgeround'>
                                Details
                            </span>
                            <SourceTxComponent
                                sourceTxHash={sourceTxHash}
                                chainId={sellData.chainId}
                            ></SourceTxComponent>
                            {!isWrapOnly(sellData, buyData) &&
                            !isUnWrapOnly(sellData, buyData) &&
                            liquiditySource !== LiquiditySource.jupiter ? (
                                <DestinationTxComponent
                                    destinationTxHash={destinationTxHash}
                                    chainId={buyData.chainId}
                                />
                            ) : (
                                <></>
                            )}
                            {!isFeatureEnabled('USE_TESTNETS_ONLY') && liquiditySource !== LiquiditySource.jupiter && (
                                <div className='flex items-center justify-between'>
                                    <span className='text-base text-mach-sub-text'>Miles earned</span>
                                    <div className='flex items-center gap-[6px]'>
                                        <PointIcon height='32' width='32' className='xxs:h-[40px] xxs:w-[40px]' />
                                        {miles !== undefined && miles !== null && miles !== 0 ? (
                                            <span className='xxs:text-[24px] xs:text-[32px] text-[20px]'>
                                                {formatNumber(miles?.toString())}
                                            </span>
                                        ) : (
                                            <div className='xxs:h-6 xs:h-8 xxs:w-20 xs:w-24 h-5 w-16 animate-pulse rounded-md bg-mach-surface-border'></div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {!isFeatureEnabled('USE_TESTNETS_ONLY') && liquiditySource !== LiquiditySource.jupiter && (
                                <div className='flex items-center justify-between'>
                                    <span className='text-base text-mach-sub-text'>Sand Dollars earned</span>
                                    <div className='flex items-center gap-[6px]'>
                                        <SandDollarIcon height='32' width='32' className='xxs:h-[40px] xxs:w-[40px]' />
                                        {sandDollars?.amount !== undefined &&
                                        sandDollars?.amount !== null &&
                                        sandDollars?.amount !== '0' ? (
                                            <span className='xxs:text-[24px] xs:text-[32px] text-[20px]'>
                                                {sandDollarsBalance?.formatWithMagnitudes(6) || '0'}
                                            </span>
                                        ) : (
                                            <div className='xxs:h-6 xs:h-8 xxs:w-20 xs:w-24 h-5 w-16 animate-pulse rounded-md bg-mach-surface-border'></div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className='mt-3'>
                <SwapModalFooter isSwapCompleted onClose={onClose} />
            </div>
        </div>
    )
}
